import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { LanguageService } from '../services/language.service';

// Matches locale codes like en-US, de-DE, fr-FR, pt-BR, etc.
const LOCALE_PATTERN = /^[a-z]{2,3}-[A-Z]{2,3}$/;

export const localeGuard: CanActivateFn = (route) => {
  const languageService = inject(LanguageService);
  const router = inject(Router);
  const locale = route.paramMap.get('locale') ?? '';

  if (!languageService.getSupportedLocales().includes(locale as any)) {
    // Use the initial URL (before any route-level redirectTo processing) to
    // recover the user's intended path. state.url reflects the post-redirect
    // URL, so e.g. /offers/?origin=X becomes /offers/home?origin=X before
    // the guard even runs (via the inner path:'' → redirectTo:'home' rule).
    const navigation = router.getCurrentNavigation();
    const initialUrl = navigation?.initialUrl;
    const rawUrl =
      typeof initialUrl === 'string' ? initialUrl : initialUrl ? router.serializeUrl(initialUrl) : '';

    const urlParts = rawUrl.split('?');
    const rawPath = urlParts[0];
    const rawQuery = urlParts.slice(1).join('?');

    const pathSegments = rawPath.split('/').filter((s) => s.length > 0);

    // If the first segment looks like an unsupported locale code, strip it so
    // the user lands on the same page under the correct locale.
    const segments = LOCALE_PATTERN.test(locale) ? pathSegments.slice(1) : pathSegments;

    const queryParams: Record<string, string> = {};
    if (rawQuery) {
      for (const pair of rawQuery.split('&')) {
        const eqIdx = pair.indexOf('=');
        if (eqIdx > 0) {
          queryParams[decodeURIComponent(pair.slice(0, eqIdx))] = decodeURIComponent(pair.slice(eqIdx + 1));
        }
      }
    }

    if (segments.length === 0) {
      return router.createUrlTree(['/', languageService.locale(), 'home']);
    }

    return router.createUrlTree(['/', languageService.locale(), ...segments], { queryParams });
  }

  languageService.setLocale(locale);
  return true;
};
